/* eslint-disable no-param-reassign */
/* eslint-disable no-unused-expressions */
import { Component, registerComponent } from 'js/component.js';
import * as LS from '../../../../locator-shared.js';

const componentName = 'partner-analytics';

export class LocatorPartnerAnalytics extends Component {

    // ==========
    // COMPONENT SETUP
    // ==========

    // Shared elements
    sharedEls = {
        // Top-level containers
        partner: this.element.querySelector(`.${componentName}__partner-container`),
    };

    // Partner elements
    partnerEls = {
        // Info Section
        infoImage: this.sharedEls.partner.querySelector(`.${componentName}__info-image`),
        infoContactButtons: this.sharedEls.partner.querySelector(`.${componentName}__info-contact-buttons`),
        trafficTableBody: this.sharedEls.partner.querySelector(`#${componentName}__traffic-table-body`),
        trafficRowTemplate: this.sharedEls.partner.querySelector(`#${componentName}__traffic-row-template`),
        trafficPagination: this.sharedEls.partner.querySelector(`#${componentName}__traffic-pagination`),
        searchTermTableBody: this.sharedEls.partner.querySelector(`#${componentName}__search-term-table-body`),
        searchTermTableHead: this.sharedEls.partner.querySelector(`#${componentName}__search-term-table-head`),
        searchTermTemplate: this.sharedEls.partner.querySelector(`#${componentName}__search-term-row-template`),
        searchTermPagination: this.sharedEls.partner.querySelector(`#${componentName}__search-term-pagination`),
        officeLocationTableBody: this.sharedEls.partner.querySelector(`#${componentName}__office-location-table-body`),
        officeLocationTableHead: this.sharedEls.partner.querySelector(`#${componentName}__office-location-table-head`),
        officeLocationTemplate: this.sharedEls.partner.querySelector(`#${componentName}__office-location-row-template`),
        officeLocationPagination: this.sharedEls.partner.querySelector(`#${componentName}__office-location-pagination`),
    };

    // Config
    config = {
        tableRowsPerPage: 10,
    };

    // State
    state = {
        partnerId: null,
        doc: null,
        map: null,
        infobox: null,
        location: { name: null, latitude: null, longitude: null }
    };

    // ==========
    // BOUND GETTER FUNCTIONS
    // ==========
    /**
     * @param {string} fieldName
     * @param {number} [arrayIndex]
     */
    getField = (fieldName, arrayIndex) => LS.getField(this.state.doc, fieldName, arrayIndex);

    /** @param {string} fieldName */
    getFieldAsArray = (fieldName) => LS.getFieldAsArray(this.state.doc, fieldName);

    getObjectAsArray = (jsonObject) => {
        if (typeof jsonObject !== 'object' || jsonObject === null) {
            return [];
        }

        const arrayOfObjects = Object.keys(jsonObject).map((key) => ({ ...jsonObject[key], key }));
        arrayOfObjects.sort((a, b) => a.key.localeCompare(b.key));

        return arrayOfObjects;
    };

    getObjectAsArrayOfValues = (jsonObject) => {
        if (typeof jsonObject !== 'object' || jsonObject === null) {
            return [];
        }
        const arrayOfObjects = Object.keys(jsonObject).map((key) => ({ ...jsonObject[key], key, value: jsonObject[key] }));
        arrayOfObjects.sort((a, b) => a.key.localeCompare(b.key));

        return arrayOfObjects;
    };

    /**
     * @param {Record<string, string>} locationFields
     * @param {number} [arrayIndex]
     * @param {boolean} [shortFormat]
     */
    getAddressString = (locationFields, arrayIndex, shortFormat) => LS.getAddressString(this.state.doc, locationFields, arrayIndex, shortFormat);

    /**
     * @param {Record<string, string>} regionFields
     * @param {number} [arrayIndex]
     */
    getRegions = (regionFields, arrayIndex) => LS.getRegions(this.state.doc, regionFields, arrayIndex);

    // ==========
    // LOAD CONTENT
    // ==========
    loadContent = async () => {
        window.dispatchEvent(new CustomEvent('show-loading'));

        // Read URL param information
        const url = new URL(window.location.href);
        this.state.partnerId = url.searchParams.get(LS.OtherUrlParams.accountId);

        const salt = this.sharedEls.partner.getAttribute('data-var-ss');
        if (salt && salt > 0) {
            this.state.partnerId = this.state.partnerId.substring(0, (this.state.partnerId.length - parseInt(salt, 10)));
        }

        if (this.sharedEls.partner.getAttribute('data-var-pdl') && this.sharedEls.partner.getAttribute('data-var-pdl').length > 0) {
            this.state.partnerDetailsLink = `${this.sharedEls.partner.getAttribute('data-var-pdl')}.html?id=${this.state.partnerId}`;
        } else {
            this.state.partnerDetailsLink = `https://www.rockwellautomation.com/en-us/sales/partner-details.html?id=${this.state.partnerId}`;
        }

        this.state.location = await LS.readEffectiveLocation();
        
        let data;
        try {
            data = await LS.fetchData({
                facets: `id:${this.state.partnerId}`,
                startIndex: 0,
                numResults: 1,
            });
        } catch {
            // Error case with no data is handled below
        }
        this.state.doc = data?.response?.docs?.[0];

        let aaData;
        try {
            aaData = await this.fetchAnalyticsData();
        } catch {
            // Error case with no data is handled below
        }
        this.state.aaData = aaData;

        if (!this.state.aaData || !this.state.doc) {
            this.showFailedToLoad();
        } else {
            await this.showPartner();
        }

        window.dispatchEvent(new CustomEvent('hide-loading'));
    };

    showFailedToLoad = () => {
        console.error('Failed to load partner data');
        LS.fillTemplate(this.sharedEls.partner, 'data-x', {
            partnerName: 'No current month data available for this partner'
        });
    };

    // ==========
    // POPULATE CONTENT
    // ==========
    /** Populate and display partner information */
    showPartner = async () => {

        // ==========
        // PRE-COMPUTE DATA
        // ==========
        // Pre-compute website links
        const websiteLinks = {
            website: this.getField(LS.Fields.Url),
        };

        // ==========
        // POPULATE CONTENT
        // ==========
        // INFO AND OVERVIEW SECTIONS
        LS.fillTemplate(this.sharedEls.partner, 'data-x', {
            // Info section
            partnerName: this.getField(LS.Fields.Name),
            partnerImgAlt: `${this.getField(LS.Fields.Name)} logo`,
            hasWebsite: Object.values(websiteLinks).some((value) => value),
            ...websiteLinks,
            hasLocatorLink: true,
            //fixme
            loactorPage: this.state.partnerDetailsLink,
            hasTraficTable: this.state.aaData?.traffic,
            hasSearchTermTable: this.state.aaData?.searchTerms,
            hasOfficeLocationTable: this.state.aaData?.officeLocations,

        });
        // Load partner logo asynchronously
        (async () => {
            const partnerLogo = await LS.getPartnerLogo(this.getField(LS.Fields.PrimaryLocation.Id), this.state.partnerId);
            if (partnerLogo) {
                this.partnerEls.infoImage.src = partnerLogo;
            }
        })();

        // Traffic table
        const trafficStats = this.getObjectAsArray(this.state.aaData?.traffic)
        .sort((a, b) => {
            try {
                const dateA = new Date(a.dateRange?.split(" to ")[0]); 
                const dateB = new Date(b.dateRange?.split(" to ")[0]); 
    
                if (isNaN(dateA) || isNaN(dateB)) {
                    return 0; 
                }
    
                return dateB - dateA;  // Newest first
            } catch (error) {
                console.error("Error sorting traffic stats:", error);
                return 0; 
            }
        })
        .map((item, index, array) => (
            this.createTrafficRow({
                item,
                index,
                array
            })
        ));

        this.partnerEls.trafficTableBody.append(...trafficStats);
        this.updateTableState(this.partnerEls.trafficTableBody, this.partnerEls.trafficPagination);

        // Search Term Table  this.state.aaData?.searchTerms
        const searchTermStatsAll = this.getObjectAsArray(this.state.aaData?.searchTerms);
        if (searchTermStatsAll.length > 0) {
            const latestSearchStats = searchTermStatsAll[(searchTermStatsAll.length - 1)];
            const searchDate = latestSearchStats.key.replace('to', ' to ');
            delete latestSearchStats.key;

            LS.fillTemplate(this.partnerEls.searchTermTableHead, 'data-x', {
                searchDate
            });

            const searchTermStats = this.getObjectAsArrayOfValues(latestSearchStats)
            .sort((a, b) => {
                try {
                    
                    const valA = a.value['Search Term Count']; 
                    const valB = b.value['Search Term Count']; 
        
                    if (isNaN(valA) || isNaN(valB)) {
                        return 0; 
                    }
        
                    return valB - valA;  // biggest first
                } catch (error) {
                    console.error("Error sorting search term stats:", error);
                    return 0; 
                }
            })
            .map((item, index, array) => (
                this.createSearchTermRow({
                    item,
                    index,
                    array
                })
            ));
            this.partnerEls.searchTermTableBody.append(...searchTermStats);
            this.updateTableState(this.partnerEls.searchTermTableBody, this.partnerEls.searchTermPagination);
        }

        // Office Locations Table  this.state.aaData?.officeLocations
        const officeLocationStatsAll = this.getObjectAsArray(this.state.aaData?.officeLocations);
        if (officeLocationStatsAll.length > 0) {
            const latestOfficeStats = officeLocationStatsAll[(officeLocationStatsAll.length - 1)];
            const searchDate = latestOfficeStats.key.replace('to', ' to '); //FIXME use current month
            delete latestOfficeStats.key;

            LS.fillTemplate(this.partnerEls.officeLocationTableHead, 'data-x', {
                searchDate
            });

            const officeLocationStats = this.getObjectAsArrayOfValues(latestOfficeStats)
            .sort((a, b) => {
                try {
                    
                    const valA = a.value['Location Address Click']; 
                    const valB = b.value['Location Address Click']; 
        
                    if (isNaN(valA) || isNaN(valB)) {
                        return 0; 
                    }
        
                    return valB - valA;  // biggest first
                } catch (error) {
                    console.error("Error sorting location stats:", error);
                    return 0; 
                }
            })
            .map((item, index, array) => (
                this.createOfficeLocationTermRow({
                    item,
                    index,
                    array
                })
            ));
            this.partnerEls.officeLocationTableBody.append(...officeLocationStats);
            this.updateTableState(this.partnerEls.officeLocationTableBody, this.partnerEls.officeLocationPagination);
        }
    };

    // ==========
    // POPULATE CONTENT HELPERS
    // ==========

    /**
     * Create traffic row for use in traffic table
     * @param {Record<string, string>} params
     * @returns {HTMLTableRowElement}
     */
    createTrafficRow = ({
        item, index, array
    }) => {
        const clone = this.partnerEls.trafficRowTemplate.content.cloneNode(true);
        const trafficObj = item ?? array[index];

        const dateA = new Date(trafficObj.dateRange?.split(" to ")[0]);
        const dateYear = dateA.getFullYear();

        LS.fillTemplate(clone, 'data-xt', {
            date: dateYear + ' ' + trafficObj.key,
            phoneClick: trafficObj.keyedData['Phone Click'],
            emailClick: trafficObj.keyedData['Email Click'],
            webClick: trafficObj.keyedData['Website Link'],
            youtubeClick: trafficObj.keyedData['Youtube Link'],
            facebookClick: trafficObj.keyedData['Facebook Link'],
            linkedinClick: trafficObj.keyedData['LinkedIn Link'],
            twitterClick: trafficObj.keyedData['Twitter Link'],
            uniqueVisitors: trafficObj.keyedData['Unique Visitors'],
            pageViews: trafficObj.keyedData['Page Views'],
        });
        return clone;
    };

    /**
     * Create search term row for use in  table
     * @param {Record<string, string>} params
     * @returns {HTMLTableRowElement}
     */
    createSearchTermRow = ({
        item, index, array
    }) => {
        const clone = this.partnerEls.searchTermTemplate.content.cloneNode(true);
        const searchObj = item ?? array[index];

        LS.fillTemplate(clone, 'data-xt', {
            searchTerm: searchObj.key,
            termCount: searchObj.value['Search Term Count'],
        });
        return clone;
    };

    /**
     * Create office location row for use in  table
     * @param {Record<string, string>} params
     * @returns {HTMLTableRowElement}
     */
    createOfficeLocationTermRow = ({
        item, index, array
    }) => {
        const clone = this.partnerEls.officeLocationTemplate.content.cloneNode(true);
        const searchObj = item ?? array[index];

        LS.fillTemplate(clone, 'data-xt', {
            address: searchObj.key,
            locationClick: searchObj.value['Location Address Click'],
            phoneClick: searchObj.value['Location Phone Click'],
            emailClick: searchObj.value['Location Email Click'],
        });
        return clone;
    };

    // ==========
    // UI LOGIC
    // ==========
    // ==========
    // Hide/show table rows and update pagination
    // ==========
    /**
     * @param {Element} tableBodyEl
     * @param {Element} paginationEl
     */
    updateTableState = (tableBodyEl, paginationEl) => {
        const tableRows = tableBodyEl.querySelectorAll('tr');

        // Validate startIndex
        if (tableBodyEl.startIndex == null || tableBodyEl.startIndex >= tableRows.length) {
            tableBodyEl.startIndex = 0;
        }

        const pageCount = Math.ceil(tableRows.length / this.config.tableRowsPerPage);
        const currentPageNum = Math.floor(tableBodyEl.startIndex / this.config.tableRowsPerPage + 1);

        LS.updatePagination(paginationEl, { currentPageNum, pageCount });

        // Show/hide appropriate rows
        tableRows.forEach((row, index) => {
            row.hidden = ((index < tableBodyEl.startIndex)
                || (index >= tableBodyEl.startIndex + this.config.tableRowsPerPage));
        });
    };

    /**
     * @param {Element} tableBodyEl
     * @param {Element} paginationEl
     */
    handleTablePagination = (tableBodyEl, paginationEl) => {
        if (tableBodyEl.startIndex == null) {
            tableBodyEl.startIndex = 0;
        }
        return LS.handlePagination({
            handlePrevButton: () => tableBodyEl.startIndex -= this.config.tableRowsPerPage,
            handleNextButton: () => tableBodyEl.startIndex += this.config.tableRowsPerPage,
            handleInput: (input) => tableBodyEl.startIndex = (input - 1) * this.config.tableRowsPerPage,
            then: () => this.updateTableState(tableBodyEl, paginationEl),
        });
    };

    fetchAnalyticsData = async () => {
        //const apiUrl = getConfig().partnerSearchUrl;
        ///bin/rockwell-automation/locator-report?partner=317fa07b-c112-9f83-cab8-12f8c2c5d6a0
        const apiUrl = `${window.origin}/bin/rockwell-automation/locator-report`;

        const defaultSearchParams = {
            partner: this.state.partnerId,
        };

        const url = new URL(apiUrl);
        const allSearchParams = { ...defaultSearchParams };
        Object.entries(allSearchParams)
            .filter(([, value]) => value != null)
            .forEach(([key, value]) => url.searchParams.append(key, value));

        const response = await fetch(url, {});
        if (!response.ok) {
            throw new Error(`Failed to fetch data from locator analitcs API. Status: ${response.status} (${response.statusText}).`);
        }
        return response.json();
    };

    // ==========
    // ADD EVENT LISTENERS
    // ==========
    addEventListeners = () => {

        // Pagination for traffic table
        this.partnerEls.trafficPagination.addEventListener(
            'submit',
            this.handleTablePagination(this.partnerEls.trafficTableBody, this.partnerEls.trafficPagination)
        );

        // Pagination for search term table
        this.partnerEls.searchTermPagination.addEventListener(
            'submit',
            this.handleTablePagination(this.partnerEls.searchTermTableBody, this.partnerEls.searchTermPagination)
        );

        // Pagination for office events table
        this.partnerEls.officeLocationPagination.addEventListener(
            'submit',
            this.handleTablePagination(this.partnerEls.officeLocationTableBody, this.partnerEls.officeLocationPagination)
        );
    };

    // ==========
    // INITIALIZE COMPONENT
    // ==========
    init = () => {

        this.addEventListeners();

        window.addEventListener('load', () => { // Wait for Adobe launch to run
            this.loadContent();
        });

        window.locatorPartnerAnalyticsInstance = this; // for debug purposes
    };
}

registerComponent(`.${componentName}`, LocatorPartnerAnalytics);
